/**
 * CostRunDrilldown — read-only per-run cost breakdown (quick-win C).
 *
 * Surfaces the wired-but-previously-unused GET /costs/runs endpoint
 * (quillApi.costs.getRun). Given a single run id, it shows the run's totals and
 * a per-pipeline breakdown (the pipelines that fired within that one engine
 * run). Purely a lookup — no mutation.
 *
 * The backend body is FLAT: `{ success, run_id, totals, by_pipeline }`, and the
 * numeric aggregates arrive from node-pg as strings, so every read passes
 * through `num`.
 */

import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography, TextField, Button, Alert, Chip } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { colors, typography } from '../../../theme/designTokens';
import DataTable, { Column } from '../../common/DataTable';
import { quillApi, CostRunResponse, CostRunPipelineRow } from '../../../services/quillAdminService';
import { num, formatCost, providerLabel } from './costFormat';

const CostRunDrilldown: React.FC<{ initialRunId?: string }> = ({ initialRunId = '' }) => {
  const [runIdInput, setRunIdInput] = useState<string>(initialRunId);
  const [result, setResult] = useState<CostRunResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Last-request-wins guard: a slow earlier lookup can't overwrite a newer one.
  const seqRef = useRef(0);

  const lookup = useCallback(async (runId: string) => {
    const id = runId.trim();
    if (!id) return;
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await quillApi.costs.getRun(id);
      if (seq === seqRef.current) setResult(data);
    } catch (e) {
      if (seq === seqRef.current) {
        setResult(null);
        setError(e instanceof Error ? e.message : 'Failed to load run.');
      }
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lookup(runIdInput);
  };

  const columns: Column<CostRunPipelineRow>[] = [
    {
      id: 'pipeline_key',
      label: 'Pipeline',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm, fontWeight: 600, wordBreak: 'break-all' }}>
          {row.pipeline_key}
        </Typography>
      ),
    },
    {
      id: 'provider',
      label: 'Provider',
      render: (row) =>
        row.provider ? <Chip label={providerLabel(row.provider)} size="small" variant="outlined" /> : '—',
    },
    { id: 'calls', label: 'Calls', align: 'right', render: (row) => num(row.calls).toLocaleString() },
    {
      id: 'tokens',
      label: 'Tokens (in / out)',
      align: 'right',
      render: (row) => `${num(row.input_tokens).toLocaleString()} / ${num(row.output_tokens).toLocaleString()}`,
    },
    { id: 'est_cost_usd', label: 'Est. cost', align: 'right', render: (row) => formatCost(row.est_cost_usd) },
  ];

  return (
    <Box
      sx={{
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: '16px',
        p: 2,
      }}
      data-testid="cost-run-drilldown"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ReceiptLongIcon sx={{ fontSize: 20, color: colors.costs.main }} />
        <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
          Look up a run
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
        Enter an engine run id to see the per-pipeline cost breakdown for that single run.
      </Typography>

      <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: result || error ? 2 : 0 }}>
        <TextField
          size="small"
          value={runIdInput}
          onChange={(e) => setRunIdInput(e.target.value)}
          placeholder="run id"
          inputProps={{ 'aria-label': 'Run id', style: { fontFamily: typography.fonts.mono } }}
          sx={{ minWidth: 280, flexGrow: 1, maxWidth: 480 }}
        />
        <Button type="submit" variant="contained" disabled={loading || !runIdInput.trim()} sx={{ textTransform: 'none' }}>
          {loading ? 'Looking up…' : 'Look up'}
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => lookup(runIdInput)} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {result && !error && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm, color: 'text.secondary', wordBreak: 'break-all' }}>
              {result.run_id}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: typography.sizes.lg }}>
              {formatCost(result.totals?.est_cost_usd)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {num(result.totals?.calls).toLocaleString()} calls ·{' '}
              {num(result.totals?.input_tokens).toLocaleString()} / {num(result.totals?.output_tokens).toLocaleString()} tokens
            </Typography>
          </Box>
          <DataTable<CostRunPipelineRow>
            columns={columns}
            data={result.by_pipeline ?? []}
            emptyMessage="No pipeline spend recorded for this run"
          />
        </Box>
      )}
    </Box>
  );
};

export default CostRunDrilldown;
